/* The block Brill emits when it rewrites a post in Refine with Brill.
 *
 * WHY A BLOCK AND NOT THE WHOLE REPLY
 * Generating a post is one-shot: the reply IS the post, because there is nothing
 * else in the conversation. Refining is a conversation — Brill says what it
 * changed and asks what else is off — so the reply carries two things at once. A
 * reply that is sometimes prose and sometimes a post cannot be told apart by
 * reading it, and guessing would either put Brill's commentary into her post box
 * or drop the rewrite on the floor.
 *
 * So the rewrite is delimited, the same way the Offer Ladder and website sections
 * do it. The block is the ONLY thing that ever replaces what's in the post box.
 *
 *   Shorter, and the ask is softer now.
 *
 *   <<<POST_REWRITTEN>>>
 *   Building a beautiful brand without a business model underneath it…
 *   <<<POST_END>>>
 *
 * NOT EVERY TURN REWRITES. "Why did you open with that?" gets an answer and no
 * block, and her post must sit untouched — which is exactly what `null` means
 * here.
 */

const BLOCK = /<<<POST_REWRITTEN>>>([\s\S]*?)<<<POST_END>>>/;

/** Did Brill rewrite the post in this reply? */
export function hasPostBlock(text) {
  return BLOCK.test(text || "");
}

/**
 * The rewritten post, or null when this reply didn't rewrite one.
 *
 * The body is kept verbatim apart from its outer whitespace: line breaks and
 * blank lines between paragraphs ARE the post's formatting on LinkedIn, and
 * collapsing them would quietly reformat every rewrite. An empty block counts as
 * no rewrite — blanking her post is never what she meant.
 */
export function parsePostBlock(text) {
  const m = BLOCK.exec(text || "");
  if (!m) return null;
  /* Fences first: a model wrapping the post in ``` is common enough that leaving
     them in would put three backticks at the top of her LinkedIn post. */
  const body = m[1].replace(/^\s*```[a-z]*\s*\n?/i, "").replace(/\n?\s*```\s*$/, "").trim();
  return body || null;
}

/**
 * The reply with the block removed — what she reads in the chat.
 *
 * The post is already on screen in the box beside the conversation, so leaving it
 * inline would show her the same post twice with delimiters around it.
 */
export function stripPostBlock(text) {
  return (text || "")
    .replace(new RegExp(BLOCK.source, "g"), "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** The refine conversation, trimmed to what's worth sending.
 *
 *  Every turn rides along on every message, and a long back-and-forth about one
 *  post is mostly superseded: the CURRENT draft is sent separately and is the
 *  real state, so the older turns only carry intent ("softer", "less corporate").
 *  Keeping the last few of those is the useful part; the rest is paid for on
 *  every message and adds nothing.
 */
export function trimRefineHistory(msgs = [], keep = 12) {
  const clean = (msgs || [])
    .filter((m) => m && (m.role === "user" || m.role === "assistant") && String(m.content || "").trim())
    .map((m) => ({ role: m.role, content: String(m.content) }));
  return clean.slice(-keep);
}
